import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/lib/apiClient";
import type {
  AiProcessingGateResponse,
  AiProcessingSummaryResponse,
  SynthesisProcessingStatus,
} from '@hector/shared-types';
import type { CaseMetadata } from '@/services/folderHierarchyService';

// Guard idempotent pour le redirect /auth sur 401 non récupérable. Le polling
// synthèse interroge N jobs en parallèle (useQueries) ; sans ce flag, plusieurs
// 401 simultanés déclencheraient chacun leur signOut() + window.location.href.
// Le premier 401 non récupérable gagne, les autres sont no-op.
let isRedirectingToAuth = false;

/**
 * Résultat d'une vérification de synthèse.
 * - `'processing'` : job en cours
 * - `'completed'`  : synthèse disponible en base
 * - `'error'`      : erreur transitoire (DB, réseau, timeout) — le polling continue
 * - `'failed'`     : échec définitif BullMQ — le polling s'arrête (consommateur affiche l'erreur)
 *
 * Actuellement le service renvoie seulement processing/completed/error. `'failed'` est
 * supporté côté type pour quand/si le backend commence à le remonter — le hook useSynthesisStatus
 * a déjà la logique défensive pour ce cas (toast + arrêt du polling).
 */
export type SynthesisStatusResult = {
  status: 'processing' | 'completed' | 'error' | 'failed';
  createdAt?: string;
  errorMessage?: string;
  error?: string;
};

interface SynthesisPayload {
  dossierID: string;
  documentID: string;
  dossierName: string;
  clientName: string;
  lawyerName: string;
  typeDeProcedure: string;
  documentCategory: string;
  documentSubcategory?: string | null; // Subcategory for partie_adverse (pieces_communiquees, actes_communiques)
  pieceNumber?: number; // Piece number for automatic numbering
  pieceDescription?: string; // AI-generated piece description
  NoTitreModifications?: boolean; // If true, prevents title modifications for "cote" documents in Droit Pénal cases
  // Vectorization controls (optional - smart defaults in backend)
  enableVectorization?: boolean; // Master switch for all vectorization
  vectorizeOriginal?: boolean; // Vectorize original document
  vectorizeSynthesis?: boolean; // Vectorize synthesis document
  chunkSize?: number; // Chunk size for embeddings
}


// ============================================================================
// BATCH SYNTHESIS INTERFACES (New Batch Processing System)
// ============================================================================

/**
 * File specification for batch synthesis submission
 * All metadata fields are per-file as required by the backend
 */
export interface BatchFile {
  documentID: string;
  dossierID: string;
  dossierName: string;
  clientName: string;
  lawyerName: string;
  typeDeProcedure: string;
  documentCategory: string;
  documentSubcategory?: string;
  pieceNumber?: number;
  pieceDescription?: string;
  enableVectorization?: boolean;
  vectorizeOriginal?: boolean;
  vectorizeSynthesis?: boolean;
  chunkSize?: number;
  NoTitreModifications?: boolean; // If true, prevents title modifications (for côté documents or user preference)
}

/**
 * Optional configuration for batch synthesis (used by caller, merged into files)
 */
export interface BatchOptions {
  documentCategory?: string;
  documentSubcategory?: string;
  chunkSize?: number;
}

interface BatchSubmitSubmittedResponse {
  success: boolean;
  status?: 'pending';
  jobId?: string;
  jobIds?: string[];
  batchSize?: number;
  estimatedTime?: string;
  error?: string;
  retryAfter?: number; // Seconds to wait before retry (for rate limit errors)
}

interface BatchSubmitHeldResponse {
  success: true;
  status: 'held';
  limit: number;
  totalSourceDocuments: number;
  heldDocumentIds: string[];
  message: string;
}

/**
 * Response from batch submission endpoint. `held` is a business state, not a
 * failed submission: the upload succeeded but dossier-level AI processing is
 * suspended until the source-document count is back within the limit.
 */
export type BatchSubmitResponse = BatchSubmitSubmittedResponse | BatchSubmitHeldResponse;

/**
 * Processing stages for synthesis batch files
 */
export type SynthesisStage = 
  | 'pending'
  | 'fetching_document'
  | 'ocr_processing'
  | 'generating_title'
  | 'generating_synthesis'
  | 'creating_docx'
  | 'vectorizing_synthesis'
  | 'vectorizing_original'
  | 'completed'
  | 'failed';

/**
 * Individual file status within a batch job
 */
export interface FileStatus {
  fileIndex: number;
  documentId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  stage: SynthesisStage;
  progress: number;
  retryCount: number;
  startedAt: string | null;
  completedAt: string | null;
  result?: {
    synthesisDocumentId: string;
    synthesisName: string;
    chunksVectorized: number;
    originalChunksVectorized?: number;
    processingTime: number;
  };
  error?: string;
}

/**
 * Overall batch job status
 */
export interface JobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'partially_completed';
  caseId: string;
  totalFiles: number;
  filesCompleted: number;
  filesFailed: number;
  progress: number; // 0-100
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  region: string;
  error?: string;
  /** Application error code (e.g. DOCUMENT_NOT_FOUND) — set by backend on job failure */
  errorCode?: string | null;
  /** User-facing French error message — preferred over `error` for display */
  errorMessage?: string | null;
}

/**
 * Response from job status endpoint
 */
export interface JobStatusResponse {
  success: boolean;
  job?: JobStatus;
  files?: FileStatus[];
  error?: string;
  /** HTTP status for non-OK polling responses; used to stop permanent 404 loops. */
  statusCode?: number;
}

/**
 * Response from queue status endpoint
 */
export interface QueueStatusResponse {
  success: boolean;
  waiting?: number;
  active?: number;
  completed?: number;
  failed?: number;
  delayed?: number;
  paused?: number;
  error?: string;
}

/**
 * Entry returned by the batch synthesis status endpoint.
 * Each entry tells whether a synthesis document exists for a given original document.
 */
export interface BatchSynthesisEntry {
  exists: boolean;
  synthesisId?: string;
  createdAt?: string;
  /** Durable, browser-safe result that survives the Redis job TTL. */
  processingStatus?: SynthesisProcessingStatus;
}

const PROCESSING_TIMEOUT_MINUTES = 10;
const MAX_BATCH_SYNTHESIS_STATUS_IDS = 200;

export class SynthesisService {
  private baseURL: string;
  private apiClient: typeof apiClient;
  private readonly CLIENT_TIMEOUT_MS = 300000; // 5 minutes
  constructor() {
    this.baseURL = import.meta.env.VITE_BACKEND_URL;
    this.apiClient = apiClient;
    if (!this.baseURL) {
      throw new Error('VITE_BACKEND_URL environment variable is not set');
    }
  }

  /**
   * Main synthesis method with simple 4-attempt retry
   */
  async synthesizeDocument(payload: SynthesisPayload): Promise<{success: boolean, error?: string}> {
    for (let attempt = 1; attempt <= 4; attempt++) {
      try {
        const result = await this.makeSynthesisRequest(payload);

        if (result.success) {
          return { success: true };
        }

        // If not the last attempt, continue to next retry
        if (attempt < 4) {
          continue;
        }

        // Last attempt failed
        return { success: false, error: result.error || 'All retry attempts failed' };

      } catch (error) {
        
        // If not the last attempt, continue to next retry
        if (attempt < 4) {
          continue;
        }
        
        // Last attempt failed with exception
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Network error on all attempts' 
        };
      }
    }

    // This should never be reached, but TypeScript needs it
    return { success: false, error: 'Unexpected error in retry loop' };
  }

  /**
   * Make a synthesis request with timeout handling
   */
  private async makeSynthesisRequest(payload: SynthesisPayload): Promise<{success: boolean, error?: string}> {
    try {
      
      // Create AbortController for client-side timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.CLIENT_TIMEOUT_MS);

      try {
        const response = await fetch(`${this.baseURL}/api/synthesize-document-enhanced`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        const result = await response.json();

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${result.error || 'Unknown error'}`);
        }

        return { success: result.success };
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: 'Client timeout - document processing exceeded 5 minutes'
        };
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  /**
   * Query the batch job endpoint for a specific file's startedAt timestamp.
   * Returns the ISO string if the file has started, null if still queued,
   * or undefined if batch info is unavailable (network error, job expired, etc.).
   */
  private async getBatchFileStartedAt(
    batchJobId: string,
    documentId: string
  ): Promise<string | null | undefined> {
    try {
      const jobStatus = await this.getJobStatus(batchJobId);
      if (!jobStatus.success || !jobStatus.files) return undefined;

      const file = jobStatus.files.find(f => f.documentId === documentId);
      if (!file) return undefined;

      return file.startedAt;
    } catch {
      return undefined;
    }
  }

  /**
   * Determine whether a document should be considered timed-out.
   * Batch-aware: if the document belongs to a batch job and hasn't started yet, no timeout.
   * If processing, timeout is measured from startedAt, not from upload time.
   */
  private async shouldConsiderTimedOut(
    documentId: string,
    createdAt: string,
    batchJobId?: string
  ): Promise<boolean> {
    const now = Date.now();
    const elapsedMinutes = (now - new Date(createdAt).getTime()) / (1000 * 60);

    if (elapsedMinutes <= PROCESSING_TIMEOUT_MINUTES) return false;

    if (!batchJobId) return true;

    const startedAt = await this.getBatchFileStartedAt(batchJobId, documentId);

    // undefined = batch info unavailable, fall back to default timeout
    if (startedAt === undefined) return true;

    // null = file hasn't started processing yet, still queued
    if (startedAt === null) return false;

    // File has started — measure timeout from actual start time
    const processingMinutes = (now - new Date(startedAt).getTime()) / (1000 * 60);
    return processingMinutes > PROCESSING_TIMEOUT_MINUTES;
  }

  /**
   * Check if synthesis document exists for a given original document ID
   * Returns completed status only if synthesis document definitively exists in database
   * Returns error status if document has been processing for more than 10 minutes (batch-aware)
   */
  async checkSynthesisExists(
    documentId: string,
    batchJobId?: string,
    submittedAt?: number
  ): Promise<SynthesisStatusResult> {
    try {
      const response = await this.apiClient.get<any>(`/documents/synthesis/${documentId}`);

      const isSynthesis = response.id && response.document_type === 'synthesis';
      const explicitExists = response.exists === true;

      if (isSynthesis || explicitExists) {
        return { status: 'completed', createdAt: response.created_at };
      }

      const docMetadata = await this.apiClient.get<any>(`/documents/${documentId}/metadata`);
      const createdAt = docMetadata.created_at || docMetadata.data?.created_at;

      if (createdAt) {
        // Use submittedAt (from Zustand store) when more recent than createdAt.
        // This prevents retried documents from being immediately timed-out
        // based on their original upload date.
        const createdAtMs = new Date(createdAt).getTime();
        const timeRef = submittedAt && submittedAt > createdAtMs
          ? new Date(submittedAt).toISOString()
          : createdAt;
        const timedOut = await this.shouldConsiderTimedOut(documentId, timeRef, batchJobId);
        if (timedOut) {
          return { status: 'error', createdAt };
        }
      }

      return { status: 'processing', createdAt };
    } catch (error) {
      console.error('[SynthesisService] Error checking synthesis:', error);
      return { status: 'error' };
    }
  }

  /**
   * Batch-check synthesis existence for multiple documents in API-sized requests.
   * Replaces N individual checkSynthesisExists calls with a small number of POST requests.
   *
   * The backend deliberately caps each request at 200 IDs. Larger dossiers must be
   * split here; truncating the list silently leaves documents without a status entry,
   * which makes the UI hide the "Prêt" badge even when their synthesis exists.
   *
   * @param documentIds - Array of original document IDs to check
   * @returns Map of documentId to synthesis existence status
   */
  async batchCheckSynthesisStatus(
    documentIds: string[]
  ): Promise<Record<string, BatchSynthesisEntry>> {
    if (documentIds.length === 0) return {};

    const batches: string[][] = [];
    for (let offset = 0; offset < documentIds.length; offset += MAX_BATCH_SYNTHESIS_STATUS_IDS) {
      batches.push(documentIds.slice(offset, offset + MAX_BATCH_SYNTHESIS_STATUS_IDS));
    }

    const batchResults = await Promise.all(
      batches.map((batch) => this.apiClient.post<Record<string, BatchSynthesisEntry>>(
        '/documents/batch-synthesis-status',
        { documentIds: batch }
      ))
    );

    return Object.assign({}, ...batchResults);
  }

  /**
   * Finalizes the AI-processing decision for documents that were already
   * uploaded successfully. The case-scoped route either submits every document
   * or puts them on hold, so storage success never depends on the AI limit.
   */
  async finalizeAiProcessingAfterUpload(
    caseId: string,
    payload: {
      documentIds: string[];
      caseMetadata: CaseMetadata;
      generateTitle: boolean;
    },
  ): Promise<AiProcessingGateResponse> {
    return this.apiClient.post<AiProcessingGateResponse>(
      `/cases/${caseId}/documents/ai-processing/finalize`,
      payload,
    );
  }

  /** Releases every document held by the dossier-level AI processing limit. */
  async releaseHeldAiProcessing(
    caseId: string,
    payload: {
      caseMetadata: CaseMetadata;
      generateTitle: boolean;
    },
  ): Promise<AiProcessingGateResponse> {
    return this.apiClient.post<AiProcessingGateResponse>(
      `/cases/${caseId}/documents/ai-processing/release`,
      payload,
    );
  }

  /** Returns the current dossier-level AI processing limit state. */
  async getAiProcessingSummary(caseId: string): Promise<AiProcessingSummaryResponse> {
    return this.apiClient.get<AiProcessingSummaryResponse>(
      `/cases/${caseId}/documents/ai-processing/summary`,
    );
  }

  /**
   * Check if embeddings (chunks) exist for a given document ID
   * Used for documents that don't need synthesis but still get vectorized (e.g., 'recherche' category)
   * Returns completed status only if at least one embedding chunk definitively exists in database
   * Returns error status if document has been processing for more than 10 minutes (batch-aware)
   */
  async checkEmbeddingsExist(
    documentId: string,
    batchJobId?: string
  ): Promise<SynthesisStatusResult> {
    try {
      const { error, count } = await supabase
        .from('case_document_embeddings')
        .select('id', { count: 'exact', head: true })
        .eq('document_id', documentId);
        
      if (error) {
        return { status: 'error' };
      }
      
      if (count && count > 0) {
        const { data: doc } = await supabase
          .from('case_documents')
          .select('created_at')
          .eq('id', documentId)
          .maybeSingle();
        
        return { status: 'completed', createdAt: doc?.created_at };
      }
      
      const { data: doc, error: docError } = await supabase
        .from('case_documents')
        .select('created_at')
        .eq('id', documentId)
        .maybeSingle();
      
      if (docError) {
        return { status: 'error' };
      }
      
      if (doc?.created_at) {
        const timedOut = await this.shouldConsiderTimedOut(documentId, doc.created_at, batchJobId);
        if (timedOut) {
          return { status: 'error', createdAt: doc.created_at };
        }
      }
      
      return { status: 'processing', createdAt: doc?.created_at };
    } catch (error) {
      return { status: 'error' };
    }
  }

  async getCaseMetadata(caseId: string, documentId: string): Promise<Omit<SynthesisPayload, 'documentID'> | null> {
    try {
      
      // Get case with basic info - only from cases table
      const { data: caseData, error: caseError } = await supabase
        .from('cases')
        .select('title, client_name, procedure_type, assigned_to')
        .eq('id', caseId)
        .single();

      if (caseError || !caseData) {
        return null;
      }

      // Get document category - if documentId is provided, get from specific document, otherwise use default
      let documentCategory = 'nos_pieces'; // Default category (should be overridden by caller when known)
      
      if (documentId) {
        const { data: documentData, error: documentError } = await supabase
          .from('case_documents')
          .select('document_category')
          .eq('id', documentId)
          .single();

        if (!documentError && documentData && documentData.document_category) {
          documentCategory = documentData.document_category;
        }
      }

              // All case-related base information comes from cases table only
        // No external table queries for lawyer information
        const metadata = {
          dossierID: caseId,
          dossierName: caseData.title || 'Dossier sans titre',
          clientName: caseData.client_name || 'Client non spécifié',
          lawyerName: caseData.assigned_to || 'Non assigné',
          typeDeProcedure: caseData.procedure_type || 'Type non spécifié',
          documentCategory
        };

      return metadata;
    } catch (error) {
      return null;
    }
  }

  // ============================================================================
  // BATCH SYNTHESIS METHODS (New Batch Processing System)
  // ============================================================================

  /**
   * Submit a batch of documents for synthesis processing
   * Backend will process up to 5 documents in parallel with regional rotation
   *
   * @param files - Array of documents to synthesize (max 5 recommended)
   * @param caseId - Case ID for all documents
   * @param options - Optional configuration (category, chunk size, metadata extraction)
   * @returns Job ID for tracking batch progress
   * 
   * @example
   * const result = await service.submitSynthesisBatch(
   *   [{ documentID: 'uuid', dossierID: 'case-id', dossierName: 'Case Name' }],
   *   'case-id',
   *   { documentCategory: 'nos_pieces' }
   * );
   */
  async submitSynthesisBatch(
    files: BatchFile[],
    caseId: string,
    options?: BatchOptions
  ): Promise<BatchSubmitResponse> {
    // Validation: Check for empty files array
    if (!files || files.length === 0) {
      return {
        success: false,
        error: 'Files array cannot be empty'
      };
    }

    // Validation: Check for invalid caseId
    if (!caseId || caseId.trim() === '') {
      return {
        success: false,
        error: 'caseId is required'
      };
    }

    // Validation: Warn if batch size exceeds recommendation
    if (files.length > 5) {
      console.warn(`[Batch] Submitting ${files.length} files (recommended max: 5). Consider splitting into multiple batches.`);
    }

    // Validation: Check for duplicate document IDs
    const documentIds = files.map(f => f.documentID);
    const uniqueIds = new Set(documentIds);
    if (uniqueIds.size !== documentIds.length) {
      return {
        success: false,
        error: 'Duplicate document IDs detected in batch'
      };
    }

    // Validation: Check for missing required fields
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file) continue;
      if (!file.documentID) {
        return {
          success: false,
          error: `File at index ${i} is missing required field: documentID`
        };
      }
      if (!file.dossierID) {
        return {
          success: false,
          error: `File at index ${i} is missing required field: dossierID`
        };
      }
      if (!file.dossierName) {
        return {
          success: false,
          error: `File at index ${i} is missing required field: dossierName`
        };
      }
    }

    try {
      console.log(`[Batch] Submitting ${files.length} document(s) for synthesis`);

      // Payload structure per backend spec - all metadata per-file
      const payload = {
        caseId,
        files: files.map(file => ({
          ...file,
          // Ensure defaults for optional fields
          enableVectorization: file.enableVectorization ?? true,
          vectorizeOriginal: file.vectorizeOriginal ?? true,
          vectorizeSynthesis: file.vectorizeSynthesis ?? true,
          chunkSize: file.chunkSize ?? options?.chunkSize ?? 8000,
          documentCategory: file.documentCategory || options?.documentCategory || 'nos_pieces',
          documentSubcategory: file.documentSubcategory || options?.documentSubcategory || null
        }))
      };

      // Create AbortController for timeout (30 seconds for submission)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(`${this.baseURL}/api/synthesis/submit-batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token && { 'Authorization': `Bearer ${session.access_token}` }),
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Parse JSON response with error handling
        let result;
        try {
          result = await response.json();
        } catch (jsonError) {
          return {
            success: false,
            error: `HTTP ${response.status}: Invalid JSON response from server`
          };
        }

        // Handle rate limit (429) specifically
        if (response.status === 429) {
          const retryAfter = result.retryAfter || 60;
          console.warn(`[Batch] Rate limit exceeded. Retry after ${retryAfter} seconds`);
          return {
            success: false,
            error: `Rate limit exceeded. Please retry in ${retryAfter} seconds`,
            retryAfter
          };
        }

        // Handle other HTTP errors
        if (!response.ok) {
          return {
            success: false,
            error: result.error || `HTTP ${response.status}: Failed to submit batch`
          };
        }

        if (result.status === 'held') {
          console.log(`[Batch] AI processing held for dossier. Source documents: ${result.totalSourceDocuments}`);
          return {
            success: true,
            status: 'held',
            limit: result.limit,
            totalSourceDocuments: result.totalSourceDocuments,
            heldDocumentIds: result.heldDocumentIds || [],
            message: result.message || 'Le traitement IA est suspendu pour ce dossier.'
          };
        }

        console.log(`[Batch] Successfully submitted. Job ID: ${result.jobId}`);

        return {
          success: true,
          status: result.status || 'pending',
          jobId: result.jobId,
          jobIds: result.jobIds,
          batchSize: result.batchSize,
          estimatedTime: result.estimatedTime
        };
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      // Handle timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: 'Request timeout - submission took longer than 30 seconds'
        };
      }

      // Handle network errors
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error submitting batch'
      };
    }
  }

  /**
   * Get status of a batch synthesis job
   * Returns both job-level progress and individual file statuses
   * 
   * @param jobId - Job ID returned from submitSynthesisBatch
   * @returns Job status with file-level details
   * 
   * @example
   * const status = await service.getJobStatus('job-id');
   * console.log(`Progress: ${status.job.progress}%`);
   * console.log(`Files: ${status.job.filesCompleted}/${status.job.totalFiles}`);
   */
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    // Validation: Check for empty jobId
    if (!jobId || jobId.trim() === '') {
      return {
        success: false,
        error: 'jobId is required'
      };
    }

    // GET authentifié avec timeout 10s, réutilisable pour le retry post-refresh.
    const fetchStatus = async (accessToken: string | undefined): Promise<Response> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      try {
        return await fetch(`${this.baseURL}/api/synthesis/jobs/${jobId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    try {
      // getSession() (supabase-js v2.100, autoRefreshToken par défaut) rafraîchit
      // déjà un access token expiré s'il est dans la marge (cf. __loadSession) — le
      // cas nominal ne produit donc jamais de 401.
      const { data: { session } } = await supabase.auth.getSession();
      let response = await fetchStatus(session?.access_token);

      // 401 = token rejeté ET getSession n'a pas pu fournir un token frais. On tente
      // UN refresh explicite avant d'abandonner (belt-and-suspenders : couvre un échec
      // de refresh transitoire ou une session encore récupérable). S'il réussit, on
      // retente le poll → la boucle continue, l'utilisateur ne voit rien. Storm 401
      // synthèse (04/06/2026) : on ne laisse jamais re-spammer un token mort sans agir.
      if (response.status === 401) {
        const { data: { session: refreshed }, error: refreshError } =
          await supabase.auth.refreshSession();
        if (!refreshError && refreshed?.access_token) {
          response = await fetchStatus(refreshed.access_token);
        }
        // Refresh impossible (session réellement expirée/révoquée) OU retry encore
        // 401 → signal EXPLICITE : déconnexion + redirect login (une seule fois, cf.
        // guard). JAMAIS un arrêt silencieux qui laisse l'avocat attendre sur une page
        // figée — soit le poll a repris, soit on l'envoie se reconnecter.
        if (response.status === 401) {
          if (!isRedirectingToAuth) {
            isRedirectingToAuth = true;
            await supabase.auth.signOut();
            if (typeof window !== 'undefined') {
              window.location.href = '/auth';
            }
          }
          return { success: false, error: 'Session expirée. Veuillez vous reconnecter.', statusCode: 401 };
        }
      }

      let result;
      try {
        result = await response.json();
      } catch {
        return { success: false, error: `HTTP ${response.status}: Invalid JSON response from server`, statusCode: response.status };
      }

      if (!response.ok) {
        return {
          success: false,
          error: result.error || `HTTP ${response.status}: Failed to get job status`,
          statusCode: response.status,
        };
      }

      return { success: true, job: result.job, files: result.files };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { success: false, error: 'Request timeout - status check took longer than 10 seconds' };
      }
      return { success: false, error: error instanceof Error ? error.message : 'Network error getting job status' };
    }
  }

  /**
   * Get current synthesis queue status
   * Useful for displaying system load indicators to users
   * 
   * @returns Queue statistics (waiting, active, completed, failed)
   * 
   * @example
   * const queue = await service.getQueueStatus();
   * if (queue.waiting > 10) {
   *   console.log('Queue is busy, processing may take longer');
   * }
   */
  async getQueueStatus(): Promise<QueueStatusResponse> {
    try {
      // Create AbortController for timeout (10 seconds for queue check)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const response = await fetch(`${this.baseURL}/api/synthesis/queue/status`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Parse JSON response with error handling
        let result;
        try {
          result = await response.json();
        } catch (jsonError) {
          return {
            success: false,
            error: `HTTP ${response.status}: Invalid JSON response from server`
          };
        }

        if (!response.ok) {
          return {
            success: false,
            error: result.error || `HTTP ${response.status}: Failed to get queue status`
          };
        }

        return {
          success: true,
          waiting: result.waiting,
          active: result.active,
          completed: result.completed,
          failed: result.failed,
          delayed: result.delayed,
          paused: result.paused
        };
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      // Handle timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: 'Request timeout - queue status check took longer than 10 seconds'
        };
      }

      // Handle network errors
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error getting queue status'
      };
    }
  }
}

/** Singleton instance for use in hooks. Avoids creating new instances per render. */
export const synthesisServiceInstance = new SynthesisService();
